import {ReactNode, useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {elevation} from "@pg-design/elevation";
import {backgroundColor, borderRadius, flex, pointer} from "@pg-design/helpers-css";
import {PlusIcon} from "@pg-design/icons";
import {skipToken} from "@reduxjs/toolkit/query";

import {CitySlug, mapParamSlugToCitySlug} from "../../../../../../config/cities";
import {FilterOfferType} from "../../../../common/app/constants/enums";
import {breakpoints} from "../../../../common/app/styles/breakpoints";
import {Card} from "../../../card/Card";
import {useGetInvestmentDetailQuery} from "../../../map/api/map_api";
import {LeafletMap} from "../../../map/LeafletMap";
import {OfferBox} from "../../../map/map_offer_box/OfferBoxBase";
import {DashboardMapFilter as Filters} from "./DashboardMapFilter";

interface IProps {
    city: CitySlug | null;
    offerType?: FilterOfferType;
    title: string | ReactNode;
    helper?: string | ReactNode;
    locked?: boolean;
    empty?: boolean;
}

export const DashboardMapChartCard = (props: IProps) => {
    const {city, locked, empty} = props;

    const citySlug = mapParamSlugToCitySlug(city ?? "");
    const [currentOfferId, setCurrentOfferId] = useState<string | null>(null);
    const investmentRes = useGetInvestmentDetailQuery(currentOfferId ?? skipToken);

    const handleOfferBoxClose = () => setCurrentOfferId(null);

    return (
        <Card area="map" locked={locked} empty={empty} chartPlaceholder="googleMap">
            <StyledContainer hasOffer={!!currentOfferId}>
                <StyledMapWrapper hasOffer={!!currentOfferId}>
                    <LeafletMap
                        city={citySlug}
                        onMarkerClick={setCurrentOfferId}
                        activeOfferId={currentOfferId}
                        cityOutlineOnly
                    />
                    <Filters />
                </StyledMapWrapper>

                {!!currentOfferId && (
                    <StyledOfferBoxSidebar>
                        <StyledOfferBoxContent>
                            <OfferBox offer={investmentRes.data ?? null} />
                        </StyledOfferBoxContent>
                        <StyledCloseButton onClick={handleOfferBoxClose}>
                            <PlusIcon size="2.2" css={closeIconRotate} />
                        </StyledCloseButton>
                    </StyledOfferBoxSidebar>
                )}
            </StyledContainer>
        </Card>
    );
};

const StyledContainer = styled.div<{hasOffer: boolean}>`
    width: 100%;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    position: relative;
    align-items: stretch;

    @media (min-width: ${breakpoints.md}) {
        flex-direction: row;
        height: 800px;
        min-height: 800px;
        gap: 0;
    }
`;

const StyledMapWrapper = styled.div<{hasOffer: boolean}>`
    width: 100%;
    height: 400px;
    min-height: 400px;
    position: relative;
    transition: width 0.3s ease-in-out;
    overflow: hidden;
    align-self: stretch;
    min-width: 0;

    @media (min-width: ${breakpoints.md}) {
        height: 100%;
        flex: ${(props: {hasOffer: boolean}) => (props.hasOffer ? "1 1 60%" : "1 1 100%")};
        min-height: 800px;
    }
`;

const StyledOfferBoxSidebar = styled.div`
    width: 100%;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    background-color: #f5f5f5;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    flex-shrink: 0;
    align-self: stretch;

    @media (min-width: ${breakpoints.md}) {
        width: 40%;
        min-height: 800px;
        background-color: #fff;
        border-left: 1px solid ${(props: {theme: Theme}) => props.theme.colors.gray[200]};
    }
`;

const StyledOfferBoxContent = styled.div`
    width: 100%;
    flex: 1;
`;

const StyledCloseButton = styled.button`
    ${backgroundColor("#fff")};
    ${elevation(2)};
    transition: background-color 0.2s ease-in-out;
    ${pointer};
    ${borderRadius(1)};
    width: 3.8rem;
    height: 3.8rem;
    ${flex("center", "center")};
    border: none;
    position: absolute;
    top: 1rem;
    right: 1rem;

    &:hover,
    &:focus,
    &:active {
        background-color: ${(props: {theme: Theme}) => props.theme.colors.gray[100]};
    }
`;

const closeIconRotate = css`
    rotate: 45deg;
`;
